/**
 * Composable for determining the component/element type to render
 * Commonly used for button-like components that can render as button, link, or router-link
 */
import { computed } from 'vue'

/**
 * Determine the appropriate element/component type based on props
 * @param {Object} props - Component props
 * @param {String} props.as - Explicit element type override
 * @param {String|Object} props.to - Router link destination
 * @param {String} props.href - External link URL
 * @param {String} defaultElement - Default element type when no specific prop is set (default: 'button')
 * @returns {Object} Object containing element type and computed type attribute
 */
export function useComponentElement(props, defaultElement = 'button') {
  /**
   * Determines which element/component to render
   * Priority: as > to (router-link) > href (a) > defaultElement
   */
  const is = computed(() => {
    if (props.as) {
      return props.as
    }

    if (props.to) {
      return 'router-link'
    }

    if (props.href) {
      return 'a'
    }

    return defaultElement
  })

  /**
   * Computes the type attribute for button elements
   * Returns null for non-button elements
   */
  const computedType = computed(() => {
    if (is.value === 'button') {
      return props.type ?? 'button'
    }

    return null
  })

  return {
    is,
    computedType
  }
}
